import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { childLogin } from '../../services/api';
import LanternMark from '../../components/LanternMark';

// Family code persists across launches so the home-screen PWA can pre-fill
// it without the child needing the original family link more than once.
const FAMILY_ID_STORAGE_KEY = 'lantern.familyId';

const COLORS = {
  bg: '#FFFDF7',
  text: '#1A1F36',
  textMuted: '#5A6478',
  amber: '#F59E0B',
  amberDark: '#D97706',
  amberSoft: '#FEF3C7',
  green: '#10B981',
  card: '#FFFFFF',
  border: '#F0E7D5',
  errorBg: '#FEF2F2',
  errorText: '#B42318',
};

const PIN_MIN = 4;
const PIN_MAX = 6;

export default function ChildLogin() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const familyIdFromUrl = searchParams.get('familyId') || '';

  // Resolve the family code seed once at mount: the URL param wins, with
  // localStorage as the home-screen fallback. Stashing in useState pins the
  // value so later writes to localStorage (e.g. via the persist effect
  // below) don't change the lock indicator mid-session.
  const [seededFamilyId] = useState(() => {
    if (familyIdFromUrl) return familyIdFromUrl;
    try {
      return localStorage.getItem(FAMILY_ID_STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });

  // Lock the field whenever a seed was found — URL link or persisted from a
  // prior visit are both authoritative. Only a fresh device with no prior
  // link leaves the field editable.
  const familyIdLocked = seededFamilyId.length > 0;

  const [username, setUsername] = useState('');
  const [familyId, setFamilyId] = useState(seededFamilyId);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [pressedKey, setPressedKey] = useState(null);

  // Persist whenever the URL carries a familyId so the next launch — for
  // example opening the installed PWA from the home screen, which uses the
  // bare start_url — pre-fills the same family from localStorage. Storage
  // failures (private browsing, quota) are silently tolerated; the link
  // still works for the current session.
  useEffect(() => {
    if (!familyIdFromUrl) return;
    try {
      localStorage.setItem(FAMILY_ID_STORAGE_KEY, familyIdFromUrl);
    } catch { /* ignore */ }
  }, [familyIdFromUrl]);

  // Desktop keyboard support for the PIN pad. Digits and Backspace feed the
  // dots display so a child (or parent on a laptop) can sign in without
  // reaching for the mouse. Skipped while a text input is focused so the
  // username and family code fields keep normal typing behaviour.
  useEffect(() => {
    function onKeyDown(e) {
      if (submitting || success) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        setError('');
        setPin((c) => (c.length >= PIN_MAX ? c : c + e.key));
        setPressedKey(e.key);
        setTimeout(() => setPressedKey(null), 140);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        setError('');
        setPin((c) => c.slice(0, -1));
        setPressedKey('del');
        setTimeout(() => setPressedKey(null), 140);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [submitting, success]);

  const canSubmit = useMemo(() => {
    return (
      username.trim().length > 0 &&
      familyId.trim().length > 0 &&
      pin.length >= PIN_MIN &&
      !submitting
    );
  }, [username, familyId, pin, submitting]);

  function handleDigit(d) {
    setError('');
    setPin((current) => (current.length >= PIN_MAX ? current : current + d));
    setPressedKey(d);
    setTimeout(() => setPressedKey(null), 140);
  }

  function handleDelete() {
    setError('');
    setPin((current) => current.slice(0, -1));
    setPressedKey('del');
    setTimeout(() => setPressedKey(null), 140);
  }

  async function handleSubmit(e) {
    if (e) e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError('');

    try {
      const res = await childLogin(username.trim().toLowerCase(), pin, familyId.trim());
      const token = res?.data?.accessToken;
      if (!token) {
        setError("Something went wrong — please try again in a moment.");
        setSubmitting(false);
        return;
      }
      localStorage.setItem('accessToken', token);
      setSuccess(true);
      setTimeout(() => navigate('/child/subjects'), 1400);
    } catch (err) {
      setPin('');
      setSubmitting(false);
      const status = err?.response?.status;
      if (status === 429) {
        setError("Too many tries — take a short break and ask a grown-up.");
      } else {
        setError("That PIN didn't work — try again!");
      }
    }
  }

  const numberPad = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    [null, '0', 'del'],
  ];

  return (
    <>
      <style>{`
        @keyframes lantern-pop-in {
          0%   { opacity: 0; transform: translateY(8px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes lantern-shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-6px); }
          40%, 80% { transform: translateX(6px); }
        }
        @keyframes lantern-dot-pop {
          0% { transform: scale(0.4); opacity: 0; }
          60% { transform: scale(1.25); opacity: 1; }
          100% { transform: scale(1.05); opacity: 1; }
        }
        @keyframes lantern-success-pulse {
          0% { transform: scale(0.6); opacity: 0; }
          60% { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes lantern-confetti {
          0% { transform: translate(-50%, 0) rotate(0deg); opacity: 1; }
          100% { transform: translate(var(--tx, 0), var(--ty, -120px)) rotate(var(--rot, 360deg)); opacity: 0; }
        }
        .lantern-input {
          width: 100%;
          padding: 14px 18px;
          font-family: 'Source Sans 3', sans-serif;
          font-size: 17px;
          color: ${COLORS.text};
          background: ${COLORS.card};
          border: 2px solid ${COLORS.border};
          border-radius: 16px;
          outline: none;
          transition: border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;
          min-height: 52px;
          box-sizing: border-box;
        }
        .lantern-input:focus {
          border-color: ${COLORS.amber};
          box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.18);
          background: #FFFEFB;
        }
        .lantern-input::placeholder { color: #B0B5C0; }
        .lantern-input:disabled {
          background: #FAF6EC;
          color: ${COLORS.textMuted};
          cursor: not-allowed;
        }
        .lantern-padbtn {
          height: 64px;
          width: 64px;
          border-radius: 50%;
          border: 2px solid ${COLORS.border};
          background: ${COLORS.card};
          color: ${COLORS.text};
          font-family: 'Nunito', sans-serif;
          font-size: 26px;
          font-weight: 800;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 90ms ease, background 160ms ease, border-color 160ms ease, box-shadow 160ms ease, color 160ms ease;
          box-shadow: 0 4px 0 ${COLORS.border}, 0 6px 12px rgba(26,31,54,0.04);
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          position: relative;
        }
        .lantern-padbtn:hover:not(:disabled) {
          background: ${COLORS.amberSoft};
          border-color: ${COLORS.amber};
          color: ${COLORS.amberDark};
        }
        .lantern-padbtn:active:not(:disabled),
        .lantern-padbtn--pressed {
          transform: translateY(3px);
          box-shadow: 0 1px 0 ${COLORS.border}, 0 2px 4px rgba(26,31,54,0.04);
          background: ${COLORS.amberSoft};
          border-color: ${COLORS.amber};
        }
        .lantern-padbtn:focus-visible {
          outline: none;
          box-shadow: 0 4px 0 ${COLORS.border}, 0 0 0 4px rgba(245, 158, 11, 0.35);
        }
        .lantern-padbtn--del {
          font-size: 20px;
          color: ${COLORS.textMuted};
        }
        .lantern-padbtn:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }
        .lantern-submit {
          width: 100%;
          min-height: 56px;
          padding: 16px 24px;
          background: ${COLORS.amber};
          color: #fff;
          border: none;
          border-radius: 18px;
          font-family: 'Nunito', sans-serif;
          font-size: 19px;
          font-weight: 800;
          letter-spacing: 0.3px;
          cursor: pointer;
          box-shadow: 0 4px 0 ${COLORS.amberDark}, 0 8px 20px rgba(245,158,11,0.35);
          transition: transform 90ms ease, background 160ms ease, box-shadow 160ms ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .lantern-submit:hover:not(:disabled) {
          background: ${COLORS.amberDark};
          box-shadow: 0 4px 0 #B45309, 0 10px 24px rgba(245,158,11,0.45);
        }
        .lantern-submit:active:not(:disabled) {
          transform: translateY(3px);
          box-shadow: 0 1px 0 ${COLORS.amberDark}, 0 4px 10px rgba(245,158,11,0.35);
        }
        .lantern-submit:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          box-shadow: 0 4px 0 ${COLORS.amberDark};
        }
        .lantern-shake { animation: lantern-shake 360ms ease; }
        .lantern-card { animation: lantern-pop-in 320ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
        .lantern-dot-fill { animation: lantern-dot-pop 220ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }
        .lantern-spin {
          width: 18px; height: 18px; border-radius: 50%;
          border: 2.5px solid rgba(255,255,255,0.4);
          border-top-color: #fff;
          animation: lantern-spin 700ms linear infinite;
        }
        @keyframes lantern-spin { to { transform: rotate(360deg); } }
        .lantern-confetti-piece {
          position: absolute;
          left: 50%;
          top: 50%;
          width: 8px;
          height: 12px;
          border-radius: 2px;
          animation: lantern-confetti 1100ms cubic-bezier(0.2, 0.6, 0.4, 1) forwards;
        }
      `}</style>

      <div
        style={{
          minHeight: '100vh',
          background: `${COLORS.bg} radial-gradient(ellipse 600px 400px at 50% -10%, rgba(245,158,11,0.12) 0%, rgba(245,158,11,0) 60%)`,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          // env(safe-area-inset-top) keeps the login card clear of the iOS
          // status bar / camera notch when launched from the home screen.
          padding: 'calc(28px + env(safe-area-inset-top)) 16px 40px',
          fontFamily: "'Source Sans 3', sans-serif",
          color: COLORS.text,
        }}
      >
        <div
          className="lantern-card"
          style={{
            width: '100%',
            maxWidth: 460,
            background: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 28,
            padding: '32px 24px 28px',
            boxShadow: '0 10px 30px rgba(26, 31, 54, 0.08), 0 2px 6px rgba(26, 31, 54, 0.04)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 0, left: 0, right: 0, height: 6,
              background: `linear-gradient(90deg, ${COLORS.amber} 0%, #FBBF24 50%, ${COLORS.amber} 100%)`,
            }}
          />

          {/* Brand */}
          <div style={{ textAlign: 'center', marginBottom: 22, position: 'relative' }}>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
              <LanternMark size={64} />
            </div>
            <h1
              style={{
                margin: 0,
                fontFamily: "'Nunito', sans-serif",
                fontSize: 28,
                fontWeight: 800,
                color: COLORS.text,
                letterSpacing: '-0.5px',
              }}
            >
              Welcome back!
            </h1>
            <p
              style={{
                margin: '6px 0 0',
                fontSize: 15,
                color: COLORS.textMuted,
                lineHeight: 1.45,
              }}
            >
              Sign in to keep learning with Lantern.
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* Username */}
            <label
              htmlFor="child-username"
              style={{
                display: 'block',
                fontFamily: "'Nunito', sans-serif",
                fontWeight: 700,
                fontSize: 14,
                marginBottom: 8,
                color: COLORS.text,
              }}
            >
              Your username
            </label>
            <input
              id="child-username"
              className="lantern-input"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
              placeholder="e.g. sam"
              value={username}
              onChange={(e) => {
                setError('');
                setUsername(e.target.value);
              }}
              disabled={submitting}
            />

            {/* Family ID */}
            <label
              htmlFor="child-family-id"
              style={{
                display: 'block',
                fontFamily: "'Nunito', sans-serif",
                fontWeight: 700,
                fontSize: 14,
                margin: '18px 0 8px',
                color: COLORS.text,
              }}
            >
              Family code
            </label>
            <input
              id="child-family-id"
              className="lantern-input"
              type="text"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
              placeholder="Ask a grown-up for this"
              value={familyId}
              onChange={(e) => {
                setError('');
                setFamilyId(e.target.value);
              }}
              disabled={submitting || familyIdLocked}
              aria-readonly={familyIdLocked || undefined}
            />
            {familyIdLocked && (
              <p
                style={{
                  margin: '8px 4px 0',
                  fontSize: 13,
                  color: COLORS.green,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span aria-hidden="true" style={{
                  width: 16, height: 16, borderRadius: '50%',
                  background: COLORS.green, color: '#fff',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 800,
                }}>✓</span>
                Family code set up for you
              </p>
            )}

            {/* PIN section */}
            <div style={{ marginTop: 22 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 10,
                }}
              >
                <label
                  style={{
                    fontFamily: "'Nunito', sans-serif",
                    fontWeight: 700,
                    fontSize: 14,
                    color: COLORS.text,
                  }}
                >
                  Your secret PIN
                </label>
                <span style={{ fontSize: 12, color: COLORS.textMuted, fontWeight: 600 }}>
                  {PIN_MIN}–{PIN_MAX} digits
                </span>
              </div>

              {/* PIN dots display */}
              <div
                className={error ? 'lantern-shake' : undefined}
                aria-live="polite"
                aria-label={`PIN: ${pin.length} of up to ${PIN_MAX} digits entered`}
                style={{
                  display: 'flex',
                  gap: 12,
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '14px 16px',
                  background: error ? '#FEF2F2' : COLORS.bg,
                  border: `2px solid ${error ? '#FCA5A5' : COLORS.border}`,
                  borderRadius: 16,
                  marginBottom: 14,
                  minHeight: 56,
                  transition: 'background 160ms ease, border-color 160ms ease',
                }}
              >
                {Array.from({ length: PIN_MAX }).map((_, i) => {
                  const filled = i < pin.length;
                  const isLatest = i === pin.length - 1;
                  return (
                    <span
                      key={i}
                      aria-hidden="true"
                      className={isLatest ? 'lantern-dot-fill' : undefined}
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        background: filled ? COLORS.amber : 'transparent',
                        border: `2px solid ${filled ? COLORS.amber : '#D9D2BF'}`,
                        boxShadow: filled ? '0 2px 6px rgba(245,158,11,0.35)' : 'none',
                        transition: filled && !isLatest ? 'background 120ms ease, border-color 120ms ease' : undefined,
                      }}
                    />
                  );
                })}
              </div>

              {/* Number pad */}
              <div
                role="group"
                aria-label="PIN number pad"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 12,
                  justifyItems: 'center',
                  marginBottom: 4,
                }}
              >
                {numberPad.flat().map((key, idx) => {
                  if (key === null) {
                    return <div key={`blank-${idx}`} aria-hidden="true" style={{ width: 64, height: 64 }} />;
                  }
                  if (key === 'del') {
                    return (
                      <button
                        key="del"
                        type="button"
                        className={`lantern-padbtn lantern-padbtn--del ${pressedKey === 'del' ? 'lantern-padbtn--pressed' : ''}`}
                        onClick={handleDelete}
                        disabled={submitting || pin.length === 0}
                        aria-label="Delete last digit"
                      >
                        <svg width="22" height="18" viewBox="0 0 22 18" fill="none">
                          <path d="M7 1 L21 1 Q21 1 21 3 L21 15 Q21 17 19 17 L7 17 L1 9 Z"
                            stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill="none" />
                          <path d="M11 6 L16 11 M16 6 L11 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </button>
                    );
                  }
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`lantern-padbtn ${pressedKey === key ? 'lantern-padbtn--pressed' : ''}`}
                      onClick={() => handleDigit(key)}
                      disabled={submitting || pin.length >= PIN_MAX}
                      aria-label={`Digit ${key}`}
                    >
                      {key}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Error */}
            {error && (
              <div
                role="alert"
                style={{
                  marginTop: 14,
                  padding: '12px 16px',
                  background: COLORS.errorBg,
                  color: COLORS.errorText,
                  borderRadius: 14,
                  fontSize: 14,
                  fontWeight: 600,
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  border: '1.5px solid #FECACA',
                }}
              >
                <span aria-hidden="true" style={{
                  width: 18, height: 18, borderRadius: '50%',
                  background: COLORS.errorText, color: '#fff',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 800, flexShrink: 0,
                }}>!</span>
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              className="lantern-submit"
              disabled={!canSubmit}
              style={{ marginTop: 18, position: 'relative' }}
            >
              {submitting ? (
                <>
                  <span className="lantern-spin" aria-hidden="true" />
                  <span>Lighting up…</span>
                </>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                    <path d="M9 1 L11 7 L17 7.5 L12.5 11.5 L14 17 L9 14 L4 17 L5.5 11.5 L1 7.5 L7 7 Z"
                      fill="#fff" stroke="#fff" strokeWidth="0.5" strokeLinejoin="round" />
                  </svg>
                  <span>Light it up</span>
                </>
              )}
            </button>
          </form>

          {/* Parent escape hatch — understated, doesn't compete with the child flow */}
          <div style={{ textAlign: 'center', marginTop: 22 }}>
            <Link
              to="/parent/login"
              style={{
                fontSize: 12,
                color: COLORS.textMuted,
                textDecoration: 'none',
                fontWeight: 500,
              }}
            >
              Are you a parent? Sign in here
            </Link>
          </div>

          {/* Success overlay */}
          {success && (
            <div
              role="status"
              aria-live="polite"
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(255, 253, 247, 0.97)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'lantern-pop-in 200ms ease both',
                zIndex: 5,
              }}
            >
              <div style={{ position: 'relative', marginBottom: 18 }}>
                <div style={{ animation: 'lantern-success-pulse 600ms cubic-bezier(0.2,0.8,0.2,1) both' }}>
                  <LanternMark size={88} />
                </div>
                {[...Array(10)].map((_, i) => {
                  const angle = (i / 10) * Math.PI * 2;
                  const dist = 70 + Math.random() * 30;
                  const colors = ['#F59E0B', '#FBBF24', '#10B981', '#1A1F36', '#FCD34D'];
                  return (
                    <div
                      key={i}
                      className="lantern-confetti-piece"
                      style={{
                        background: colors[i % colors.length],
                        animationDelay: `${i * 30}ms`,
                        '--tx': `${Math.cos(angle) * dist}px`,
                        '--ty': `${Math.sin(angle) * dist - 20}px`,
                        '--rot': `${360 + Math.random() * 360}deg`,
                      }}
                    />
                  );
                })}
              </div>
              <div style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: 22,
                fontWeight: 800,
                color: COLORS.text,
              }}>
                You're in! ✨
              </div>
              <div style={{ fontSize: 14, color: COLORS.textMuted, marginTop: 4 }}>
                Loading your subjects…
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
